import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Check, Copy, ArrowRight, Building2, User, Mail,
  Brain, Users, Target, MessageSquare, Plus, Trash2,
} from 'lucide-react';
import { getInitials } from '@/lib/utils';

interface CreateClientFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

// ── Step types ────────────────────────────────────────────────────
type Step =
  | 'org'
  | 'user'
  | 'complete'
  | 'k_profile'
  | 'k_people'
  | 'k_goals'
  | 'k_dynamics'
  | 'k_review';

const K_STEPS: Step[] = ['k_profile', 'k_people', 'k_goals', 'k_dynamics', 'k_review'];
const K_STEP_LABELS = ['Org Profile', 'Key People', 'Goals', 'Dynamics', 'Review'];

// ── Key person interface ──────────────────────────────────────────
interface KeyPerson {
  localId: string;
  user_id: string | null;    // null = not a Cosmo user
  email: string;
  name: string;
  title: string;
  department: string;
  isFromProfile: boolean;
  disposition: string;
  influence_level: string;
  ai_fluency_level: string;
  notes: string;
}

interface OrgMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  department: string;
  is_active: boolean;
}

function newPerson(): KeyPerson {
  return {
    localId: Math.random().toString(36).slice(2),
    user_id: null, email: '', name: '', title: '', department: '',
    isFromProfile: false, disposition: 'stakeholder',
    influence_level: 'medium', ai_fluency_level: 'L1', notes: '',
  };
}

// ── Constants ─────────────────────────────────────────────────────
const DEPARTMENTS = [
  'Business Development',
  'Consulting',
  'Strategy',
  'Events',
  'Ops',
  'Leadership',
];

export function CreateClientFlow({ open, onOpenChange, onComplete }: CreateClientFlowProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('org');
  const [loading, setLoading] = useState(false);

  // ── Existing org form state (unchanged) ──────────────────────────
  const [orgName, setOrgName] = useState('');
  const [orgStatus, setOrgStatus] = useState<string>('pilot');
  const [orgNotes, setOrgNotes] = useState('');
  const [createdOrg, setCreatedOrg] = useState<{ id: string; name: string; invite_code: string } | null>(null);

  // ── Existing user form state (unchanged) ─────────────────────────
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [userDepartment, setUserDepartment] = useState('Leadership');

  // ── Existing result state (unchanged) ────────────────────────────
  const [result, setResult] = useState<{ magic_link: string; email_template: string } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // ── Knowledge ingestion state ────────────────────────────────────
  const [kIndustry, setKIndustry] = useState('');
  const [kCompanySize, setKCompanySize] = useState('');
  const [kHqLocation, setKHqLocation] = useState('');
  const [kPlanDetails, setKPlanDetails] = useState('');

  const [kPeople, setKPeople] = useState<KeyPerson[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [showPersonPicker, setShowPersonPicker] = useState(false);

  const [kPrimaryGoal, setKPrimaryGoal] = useState('');
  const [kSuccessMetric, setKSuccessMetric] = useState('');
  const [kKeyDeadline, setKKeyDeadline] = useState('');
  const [kExpansionTrigger, setKExpansionTrigger] = useState('');

  const [kDynamicsNotes, setKDynamicsNotes] = useState('');
  const [kGoogleDocUrl, setKGoogleDocUrl] = useState('');

  const [kSaving, setKSaving] = useState(false);

  // ── Reset ─────────────────────────────────────────────────────────
  const resetForm = () => {
    setStep('org');
    setOrgName('');
    setOrgStatus('pilot');
    setOrgNotes('');
    setCreatedOrg(null);
    setUserEmail('');
    setUserFullName('');
    setUserDepartment('Leadership');
    setResult(null);
    setCopiedLink(false);
    setCopiedEmail(false);
    setKIndustry('');
    setKCompanySize('');
    setKHqLocation('');
    setKPlanDetails('');
    setKPeople([]);
    setOrgMembers([]);
    setShowPersonPicker(false);
    setKPrimaryGoal('');
    setKSuccessMetric('');
    setKKeyDeadline('');
    setKExpansionTrigger('');
    setKDynamicsNotes('');
    setKGoogleDocUrl('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // ── Load org members when entering k_people step ─────────────────
  useEffect(() => {
    if (step !== 'k_people' || !createdOrg) return;
    supabase.rpc('get_org_users_admin', { _org_id: createdOrg.id }).then(({ data }) => {
      const members: OrgMember[] = (data || []) as OrgMember[];
      setOrgMembers(members);
      // Auto-add any members not already in the list
      setKPeople(prev => {
        if (prev.some(p => p.isFromProfile)) return prev;
        return [
          ...prev,
          ...members.map(m => ({
            localId:          Math.random().toString(36).slice(2),
            user_id:          m.user_id,
            email:            m.email,
            name:             m.full_name || m.email,
            title:            '',
            department:       m.department ?? '',
            isFromProfile:    true,
            disposition:      'stakeholder' as const,
            influence_level:  'medium' as const,
            ai_fluency_level: 'L1' as const,
            notes:            '',
          })),
        ];
      });
    });
  }, [step, createdOrg]);

  // ── Existing handlers (unchanged) ────────────────────────────────
  const handleCreateOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-create-org', {
        body: {
          name: orgName.trim(),
          status: orgStatus,
          internal_notes: orgNotes || null,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to create organization');
      setCreatedOrg({ id: data.org_id, name: data.org_name, invite_code: data.invite_code });
      setStep('user');
      toast.success('Organization created!');
    } catch (error: any) {
      console.error('Error creating org:', error);
      toast.error(error.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!createdOrg) {
      toast.error('Organization not found');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-create-user', {
        body: {
          email: userEmail.trim(),
          org_id: createdOrg.id,
          role: 'client_admin',
          department: userDepartment,
          full_name: userFullName.trim() || null,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to create user');
      setResult({ magic_link: data.magic_link, email_template: data.email_template });
      setStep('complete');
      toast.success('Client admin created!');
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'link' | 'email') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      }
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDone = () => {
    handleClose();
    onComplete();
  };

  // ── Knowledge save (batch, on final review step) ──────────────────
  const saveAllKnowledge = async () => {
    if (!createdOrg) return;
    setKSaving(true);
    try {
      const inserts: Array<{
        org_id: string;
        field_type: string;
        content_json: Record<string, string | boolean | undefined>;
        source_label: string;
      }> = [];

      if (kIndustry || kCompanySize || kHqLocation || kPlanDetails) {
        inserts.push({
          org_id: createdOrg.id,
          field_type: 'org_profile',
          content_json: {
            industry: kIndustry,
            company_size: kCompanySize,
            hq_location: kHqLocation,
            plan_details: kPlanDetails,
          },
          source_label: 'Initial setup',
        });
      }

      kPeople.filter(p => p.name.trim()).forEach(p => {
        inserts.push({
          org_id: createdOrg.id,
          field_type: 'key_person',
          content_json: {
            user_id:          p.user_id ?? undefined,
            email:            p.email,
            name:             p.name,
            title:            p.title,
            department:       p.department,
            disposition:      p.disposition,
            influence_level:  p.influence_level,
            ai_fluency_level: p.ai_fluency_level,
            notes:            p.notes,
          },
          source_label: 'Initial setup',
        });
      });

      if (kPrimaryGoal || kSuccessMetric) {
        inserts.push({
          org_id: createdOrg.id,
          field_type: 'goal',
          content_json: {
            primary_goal: kPrimaryGoal,
            success_metric_90d: kSuccessMetric,
            key_deadline: kKeyDeadline,
            expansion_trigger: kExpansionTrigger,
          },
          source_label: 'Initial setup',
        });
      }

      if (kDynamicsNotes || kGoogleDocUrl) {
        inserts.push({
          org_id: createdOrg.id,
          field_type: 'dynamic',
          content_json: {
            notes: kDynamicsNotes,
            google_doc_url: kGoogleDocUrl,
          },
          source_label: 'Initial setup',
        });
      }

      if (inserts.length > 0) {
        const { error } = await supabase
          .from('org_knowledge_store')
          .insert(inserts as any);
        if (error) throw error;
      }

      // Mark knowledge setup complete on the org
      const { error: updateError } = await supabase
        .from('organizations')
        .update({ knowledge_store_complete: true })
        .eq('id', createdOrg.id);
      if (updateError) throw updateError;

      const count = inserts.length;
      toast.success(`Knowledge saved — ${count} record${count !== 1 ? 's' : ''} added to Cosmo's context`);
      handleClose();
      onComplete();
      navigate(`/admin/org/${createdOrg.id}?tab=knowledge`);
    } catch (error: any) {
      console.error('Error saving knowledge:', error);
      toast.error('Failed to save knowledge');
    } finally {
      setKSaving(false);
    }
  };

  // ── Person helpers ────────────────────────────────────────────────
  const updatePerson = (localId: string, field: keyof KeyPerson, value: string) => {
    setKPeople(prev => prev.map(p => p.localId === localId ? { ...p, [field]: value } : p));
  };

  const removePerson = (localId: string) => {
    setKPeople(prev => prev.filter(p => p.localId !== localId));
  };

  // ── Knowledge progress bar ────────────────────────────────────────
  const isKnowledgeStep = K_STEPS.includes(step);
  const kStepIdx = K_STEPS.indexOf(step); // -1 if not a knowledge step
  const kPct = isKnowledgeStep ? ((kStepIdx + 1) / K_STEPS.length) * 100 : 0;

  const KnowledgeProgress = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
        <span className="font-medium">{createdOrg?.name}</span>
        <span>
          Step {kStepIdx + 1} of {K_STEPS.length}: {K_STEP_LABELS[kStepIdx]}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300 rounded-full"
          style={{ width: `${kPct}%` }}
        />
      </div>
    </div>
  );

  // ── Disposition badge colour helper ───────────────────────────────
  const dispositionColor = (d: string) => {
    if (d === 'champion' || d === 'council_admin') return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30';
    if (d === 'blocker') return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30';
    if (d === 'skeptical') return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30';
    return '';
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={
          isKnowledgeStep
            ? 'sm:max-w-2xl max-h-[90vh] overflow-y-auto'
            : 'sm:max-w-lg'
        }
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'org'       && <><Building2    className="h-5 w-5 text-primary" />Create Organization</>}
            {step === 'user'      && <><User          className="h-5 w-5 text-primary" />Create Client Admin</>}
            {step === 'complete'  && <><Mail          className="h-5 w-5 text-primary" />Send Invite</>}
            {step === 'k_profile' && <><Building2    className="h-5 w-5 text-primary" />Knowledge Setup</>}
            {step === 'k_people'  && <><Users         className="h-5 w-5 text-primary" />Knowledge Setup</>}
            {step === 'k_goals'   && <><Target        className="h-5 w-5 text-primary" />Knowledge Setup</>}
            {step === 'k_dynamics'&& <><MessageSquare className="h-5 w-5 text-primary" />Knowledge Setup</>}
            {step === 'k_review'  && <><Brain         className="h-5 w-5 text-primary" />Knowledge Setup</>}
          </DialogTitle>
        </DialogHeader>

        {/* ────────────────────────────────────────────────────────────
            EXISTING STEP 1: Create Org
        ──────────────────────────────────────────────────────────── */}
        {step === 'org' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization Name *</Label>
              <Input
                id="org-name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Acme Corp"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-status">Status</Label>
              <Select value={orgStatus} onValueChange={setOrgStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pilot">Pilot</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-notes">Internal Notes</Label>
              <Textarea
                id="org-notes"
                value={orgNotes}
                onChange={(e) => setOrgNotes(e.target.value)}
                placeholder="Optional notes about this client..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleCreateOrg} disabled={loading || !orgName.trim()}>
                {loading ? 'Creating...' : 'Next'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            EXISTING STEP 2: Create Admin User
        ──────────────────────────────────────────────────────────── */}
        {step === 'user' && createdOrg && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              Creating admin for <strong>{createdOrg.name}</strong>
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-email">Email *</Label>
              <Input
                id="user-email"
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="admin@acme.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-name">Full Name</Label>
              <Input
                id="user-name"
                value={userFullName}
                onChange={(e) => setUserFullName(e.target.value)}
                placeholder="John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-dept">Department</Label>
              <Select value={userDepartment} onValueChange={setUserDepartment}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setStep('org')}>
                Back
              </Button>
              <Button onClick={handleCreateUser} disabled={loading || !userEmail.trim()}>
                {loading ? 'Creating...' : 'Create & Generate Link'}
              </Button>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            EXISTING STEP 3: Magic Link + Email Template
            New: "Set Up Knowledge" prompt appended below Done button
        ──────────────────────────────────────────────────────────── */}
        {step === 'complete' && result && (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
              ✓ Organization and admin user created successfully!
            </div>

            <div className="space-y-2">
              <Label>Magic Link</Label>
              <div className="flex gap-2">
                <Input
                  value={result.magic_link}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(result.magic_link, 'link')}
                >
                  {copiedLink ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Template</Label>
              <Textarea
                value={result.email_template}
                readOnly
                rows={8}
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => copyToClipboard(result.email_template, 'email')}
              >
                {copiedEmail ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Copy className="h-4 w-4 mr-2" />}
                Copy Email Template
              </Button>
            </div>

            {/* Knowledge setup prompt — new addition below the existing content */}
            <div className="border-t pt-4 space-y-3">
              <div>
                <p className="text-sm font-medium">Set up Cosmo's knowledge base?</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Add org context, key people, and goals so Cosmo can make smarter
                  recommendations for <strong>{createdOrg?.name}</strong>. Takes ~3 minutes.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleDone} className="flex-1">
                  Done for Now
                </Button>
                <Button onClick={() => setStep('k_profile')} className="flex-1">
                  <Brain className="h-4 w-4 mr-2" />
                  Set Up Knowledge
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            KNOWLEDGE STEP 1: Org Profile
        ──────────────────────────────────────────────────────────── */}
        {step === 'k_profile' && (
          <div className="space-y-5">
            <KnowledgeProgress />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input
                  value={kIndustry}
                  onChange={e => setKIndustry(e.target.value)}
                  placeholder="e.g. HR Tech"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Size</Label>
                <Select value={kCompanySize} onValueChange={setKCompanySize}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-50">1–50</SelectItem>
                    <SelectItem value="51-200">51–200</SelectItem>
                    <SelectItem value="201-500">201–500</SelectItem>
                    <SelectItem value="501-1000">501–1,000</SelectItem>
                    <SelectItem value="1000+">1,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>HQ Location</Label>
              <Input
                value={kHqLocation}
                onChange={e => setKHqLocation(e.target.value)}
                placeholder="e.g. New York, NY"
              />
            </div>

            <div className="space-y-2">
              <Label>Current Plan / Deal Context</Label>
              <Input
                value={kPlanDetails}
                onChange={e => setKPlanDetails(e.target.value)}
                placeholder="e.g. Pilot (5 → 30 seats), 90-day contract"
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep('complete')}>
                ← Back
              </Button>
              <Button onClick={() => setStep('k_people')}>
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            KNOWLEDGE STEP 2: Key People
        ──────────────────────────────────────────────────────────── */}
        {step === 'k_people' && (
          <div className="space-y-5">
            <KnowledgeProgress />

            <div className="space-y-3">
              {kPeople.length === 0 && (
                <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4 text-center">
                  Loading members…
                </div>
              )}

              {kPeople.map((person) => (
                <div key={person.localId} className="border rounded-lg p-4 space-y-3 bg-muted/20">
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-2">
                    {person.isFromProfile ? (
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                          {getInitials(person.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{person.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{person.email}</p>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0 border-primary/30 text-primary">In Cosmo</Badge>
                      </div>
                    ) : (
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-1">
                        Not in Cosmo
                      </span>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => removePerson(person.localId)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Name + Title — editable only for non-profile people */}
                  {!person.isFromProfile ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Name *</Label>
                        <Input value={person.name} onChange={e => updatePerson(person.localId, 'name', e.target.value)} placeholder="Sarah Chen" className="h-8 text-sm" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Title / Role</Label>
                        <Input value={person.title} onChange={e => updatePerson(person.localId, 'title', e.target.value)} placeholder="VP Product" className="h-8 text-sm" />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Title / Role</Label>
                      <Input value={person.title} onChange={e => updatePerson(person.localId, 'title', e.target.value)} placeholder="e.g. Head of Strategy" className="h-8 text-sm" />
                    </div>
                  )}

                  {/* Disposition + Influence + Fluency */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Disposition</Label>
                      <Select value={person.disposition} onValueChange={v => updatePerson(person.localId, 'disposition', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="champion">Champion</SelectItem>
                          <SelectItem value="council_admin">Council Admin</SelectItem>
                          <SelectItem value="stakeholder">Stakeholder</SelectItem>
                          <SelectItem value="skeptical">Skeptical</SelectItem>
                          <SelectItem value="blocker">Blocker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Influence</Label>
                      <Select value={person.influence_level} onValueChange={v => updatePerson(person.localId, 'influence_level', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">AI Fluency</Label>
                      <Select value={person.ai_fluency_level} onValueChange={v => updatePerson(person.localId, 'ai_fluency_level', v)}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="L0">L0 – None</SelectItem>
                          <SelectItem value="L1">L1 – Learning</SelectItem>
                          <SelectItem value="L2">L2 – Practicing</SelectItem>
                          <SelectItem value="L3">L3 – Fluent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={person.notes}
                      onChange={e => updatePerson(person.localId, 'notes', e.target.value)}
                      placeholder="What should Cosmo know about this person's relationship to the rollout?"
                      rows={2}
                      className="text-sm resize-none"
                    />
                  </div>
                </div>
              ))}

              <Button variant="outline" className="w-full" onClick={() => setShowPersonPicker(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Person
              </Button>
            </div>

            {/* Person picker sheet */}
            {showPersonPicker && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Add a person</p>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowPersonPicker(false)}>✕</Button>
                </div>
                {/* Existing members */}
                {orgMembers.filter(m => !kPeople.some(kp => kp.user_id === m.user_id)).map(m => (
                  <button
                    key={m.user_id}
                    onClick={() => {
                      setKPeople(prev => [...prev, {
                        localId: Math.random().toString(36).slice(2),
                        user_id: m.user_id, email: m.email,
                        name: m.full_name || m.email, title: '', department: m.department ?? '',
                        isFromProfile: true, disposition: 'stakeholder',
                        influence_level: 'medium', ai_fluency_level: 'L1', notes: '',
                      }]);
                      setShowPersonPicker(false);
                    }}
                    className="w-full text-left flex items-center gap-2.5 px-3 py-2 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                      {getInitials(m.full_name || m.email)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{m.full_name || m.email}</p>
                      <p className="text-xs text-muted-foreground">{m.role}</p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0 border-primary/30 text-primary">In Cosmo</Badge>
                  </button>
                ))}
                <div className={orgMembers.filter(m => !kPeople.some(kp => kp.user_id === m.user_id)).length > 0 ? 'border-t pt-2' : ''}>
                  <Button
                    variant="ghost" size="sm" className="w-full text-muted-foreground"
                    onClick={() => { setKPeople(prev => [...prev, newPerson()]); setShowPersonPicker(false); }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />Add someone not in Cosmo
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep('k_profile')}>
                ← Back
              </Button>
              <Button onClick={() => setStep('k_goals')}>
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            KNOWLEDGE STEP 3: Goals & Success Criteria
        ──────────────────────────────────────────────────────────── */}
        {step === 'k_goals' && (
          <div className="space-y-5">
            <KnowledgeProgress />

            <div className="space-y-2">
              <Label>Primary goal in 90 days</Label>
              <Textarea
                value={kPrimaryGoal}
                onChange={e => setKPrimaryGoal(e.target.value)}
                placeholder="e.g. 80% of the pilot group actively using Cosmo weekly, enough evidence to build an ROI case for the CFO"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Key success metric</Label>
              <Input
                value={kSuccessMetric}
                onChange={e => setKSuccessMetric(e.target.value)}
                placeholder="e.g. 80% WAU, 20+ prompts submitted by month 2"
              />
            </div>

            <div className="space-y-2">
              <Label>
                Target deadline{' '}
                <span className="text-muted-foreground text-xs font-normal">(optional)</span>
              </Label>
              <Input
                value={kKeyDeadline}
                onChange={e => setKKeyDeadline(e.target.value)}
                placeholder="e.g. Q2 board meeting, June 30"
              />
            </div>

            <div className="space-y-2">
              <Label>
                What needs to happen to unlock expansion?{' '}
                <span className="text-muted-foreground text-xs font-normal">(optional)</span>
              </Label>
              <Textarea
                value={kExpansionTrigger}
                onChange={e => setKExpansionTrigger(e.target.value)}
                placeholder="e.g. CFO sign-off after seeing usage data, 80% activation confirmed in pilot group"
                rows={2}
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep('k_people')}>
                ← Back
              </Button>
              <Button onClick={() => setStep('k_dynamics')}>
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            KNOWLEDGE STEP 4: Org Dynamics & Reference Doc
        ──────────────────────────────────────────────────────────── */}
        {step === 'k_dynamics' && (
          <div className="space-y-5">
            <KnowledgeProgress />

            <div className="space-y-2">
              <Label>Internal dynamics</Label>
              <p className="text-xs text-muted-foreground">
                Who are the real champions and blockers? Political landscape, culture, anything
                Cosmo should factor in before making recommendations.
              </p>
              <Textarea
                value={kDynamicsNotes}
                onChange={e => setKDynamicsNotes(e.target.value)}
                placeholder="e.g. Sales team is most motivated — they see immediate ROI. CFO controls expansion budget and needs hard numbers before he'll approve. Legal flagged a data privacy concern that needs addressing before we engage them."
                rows={5}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Reference doc link{' '}
                <span className="text-muted-foreground text-xs font-normal">(optional)</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Paste a read-only link to call notes, a strategy doc, or an org chart. Cosmo
                will reference it on demand — nothing is stored raw.
              </p>
              <Input
                value={kGoogleDocUrl}
                onChange={e => setKGoogleDocUrl(e.target.value)}
                placeholder="https://docs.google.com/..."
                type="url"
              />
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep('k_goals')}>
                ← Back
              </Button>
              <Button onClick={() => setStep('k_review')}>
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            KNOWLEDGE STEP 5: Review & Save
        ──────────────────────────────────────────────────────────── */}
        {step === 'k_review' && (
          <div className="space-y-5">
            <KnowledgeProgress />

            <p className="text-sm text-muted-foreground">
              Review what Cosmo will know about{' '}
              <strong className="text-foreground">{createdOrg?.name}</strong> before saving.
              Anything empty can be added later from the org's knowledge store.
            </p>

            <div className="space-y-3">
              {/* Org Profile */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    Org Profile
                  </span>
                  {(kIndustry || kCompanySize || kHqLocation || kPlanDetails) ? (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                      Added
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      Empty
                    </Badge>
                  )}
                </div>
                {(kIndustry || kCompanySize || kHqLocation || kPlanDetails) ? (
                  <p className="text-xs text-muted-foreground">
                    {[kIndustry, kCompanySize, kHqLocation, kPlanDetails].filter(Boolean).join(' · ')}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Not filled in</p>
                )}
              </div>

              {/* Key People */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    Key People
                  </span>
                  {kPeople.filter(p => p.name.trim()).length > 0 ? (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                      {kPeople.filter(p => p.name.trim()).length} added
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      Empty
                    </Badge>
                  )}
                </div>
                {kPeople.filter(p => p.name.trim()).length > 0 ? (
                  <div className="space-y-1">
                    {kPeople.filter(p => p.name.trim()).map(p => (
                      <p key={p.localId} className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{p.name}</span>
                        {p.title && ` (${p.title})`}
                        {' · '}
                        <span className="capitalize">{p.disposition.replace('_', ' ')}</span>
                        {' · '}
                        {p.influence_level} influence
                        {' · '}
                        {p.ai_fluency_level}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Not filled in</p>
                )}
              </div>

              {/* Goals */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                    Goals
                  </span>
                  {(kPrimaryGoal || kSuccessMetric) ? (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                      Added
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      Empty
                    </Badge>
                  )}
                </div>
                {(kPrimaryGoal || kSuccessMetric) ? (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {kPrimaryGoal || kSuccessMetric}
                    {kKeyDeadline && ` · Target: ${kKeyDeadline}`}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Not filled in</p>
                )}
              </div>

              {/* Dynamics */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    Dynamics & Knowledge
                  </span>
                  {(kDynamicsNotes || kGoogleDocUrl) ? (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                      Added
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      Empty
                    </Badge>
                  )}
                </div>
                {(kDynamicsNotes || kGoogleDocUrl) ? (
                  <div className="space-y-1">
                    {kDynamicsNotes && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{kDynamicsNotes}</p>
                    )}
                    {kGoogleDocUrl && (
                      <p className="text-xs text-blue-500">Reference doc linked ✓</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Not filled in</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 gap-3">
              <Button variant="ghost" onClick={() => setStep('k_dynamics')}>
                ← Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleDone}>
                  Skip for Now
                </Button>
                <Button onClick={saveAllKnowledge} disabled={kSaving}>
                  {kSaving ? 'Saving...' : 'Save Knowledge & Finish'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
